import { useState } from "react";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import { parseCortevaCrcWorkbook } from "../utils/cortevaCrcWorkbook";
import CortevaApprovalControls from "../components/CortevaApprovalControls";

const pkr = (value, digits = 0) => `PKR ${Number(value || 0).toLocaleString("en-PK", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export default function CortevaCrcUpload() {
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [period, setPeriod] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function chooseFile(event) {
    const file = event.target.files?.[0];
    setParsed(null);
    setFileName(file?.name || "");
    setMessage("");
    if (!file) return;
    try {
      const result = parseCortevaCrcWorkbook(await file.arrayBuffer());
      setParsed(result);
      setPeriod(result.period);
      setMessage(`${result.headCount} CRC employees loaded from ${result.sheetName}. Source salary reconciled to ${pkr(result.salaryTotal)}.`);
    } catch (error) {
      setMessage(error.message);
    }
  }

  async function download(type, token) {
    const response = await fetch(`${API_BASE_URL}/invoices/download`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ invoiceType: type, segmentKey: "crc", invoiceNumber: invoiceNumber.trim(), invoiceDate, period: period.trim(), rows: parsed.rows })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `CRC PDF failed (${response.status}).`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Corteva-CRC-${type.endsWith("detail") ? "Detail" : "GST"}-${invoiceNumber.trim().replace(/[^a-zA-Z0-9_-]/g, "-")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  async function generate() {
    setBusy(true);
    try {
      if (!parsed || !invoiceNumber.trim() || !invoiceDate || !period.trim()) throw new Error("Upload the CRC sheet and enter invoice number, date, and payroll month.");
      const user = auth.currentUser;
      if (!user) throw new Error("Sign in before generating PDFs.");
      const token = await user.getIdToken();
      await download("corteva-manual-summary", token);
      await download("corteva-manual-detail", token);
      setMessage("CRC GST invoice and detail PDF downloaded. Review both before sending.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return <>
    <section className="panel"><div className="panel-header"><div><h2 className="panel-title">CRC source workbook</h2><p className="page-copy">Upload the client’s CRC salary sheet. The builder adds the fixed GLI, PESSI, EOBI, and service-charge values used by finance.</p></div></div><div className="panel-body"><div className="field"><label>Choose CRC workbook (.xlsx)</label><input className="input" type="file" accept=".xlsx" onChange={chooseFile} /></div>{fileName && <p className="page-copy">{fileName}</p>}
      {parsed && <div className="summary-grid" style={{ marginTop: 18 }}><div className="stat-card"><span>Employees</span><strong>{parsed.headCount}</strong></div><div className="stat-card"><span>Source salary</span><strong>{pkr(parsed.salaryTotal)}</strong></div><div className="stat-card"><span>Reimbursement</span><strong>{pkr(parsed.reimbursement)}</strong></div><div className="stat-card"><span>Service charges</span><strong>{pkr(parsed.serviceCharges)}</strong></div><div className="stat-card"><span>GST 16%</span><strong>{pkr(parsed.gst)}</strong></div><div className="stat-card"><span>Gross invoice</span><strong>{pkr(parsed.total)}</strong></div></div>}
    </div></section>
    <section className="panel" style={{ marginTop: 24 }}><div className="panel-header"><div><h2 className="panel-title">Invoice details</h2><p className="page-copy">The GST PDF uses the approved five-column format.</p></div></div><div className="panel-body"><div className="form-grid"><div className="field"><label>Invoice number *</label><input className="input" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} /></div><div className="field"><label>Invoice date *</label><input className="input" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} /></div><div className="field"><label>Payroll month *</label><input className="input" value={period} onChange={(event) => setPeriod(event.target.value)} placeholder="Aug-26" /></div></div><button className="button button-primary" type="button" disabled={busy || !parsed} onClick={generate}>{busy ? "Generating PDFs…" : "Generate CRC GST and Detail PDFs"}</button>{parsed && <CortevaApprovalControls invoice={{ invoiceNumber, invoiceDate, period, segmentKey: "crc", segmentName: "CRC Staff", headCount: parsed.headCount, reimbursement: parsed.reimbursement, serviceCharges: parsed.serviceCharges, invoiceAmount: Number(parsed.reimbursement || 0) + Number(parsed.serviceCharges || 0), gstAmount: parsed.gst, grossAmount: parsed.total }} />}{message && <p className="page-copy" role="status" style={{ marginTop: 12 }}>{message}</p>}</div></section>
  </>;
}
